import { useEffect, useState, useCallback, useRef } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box } from "@mui/material";
import { QuotaEntry } from "./QuotaEntry";
import { useOktaAuth } from "@okta/okta-react";
import { formatBytes } from "../utils/ByteConverter";
import { debounce } from "@mui/material/utils";

export const QuotaTable = ({ areaId, resourceType }) => {
  const { authState } = useOktaAuth();

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [departmentNumbers, setDepartmentNumbers] = useState([]);
  const [claimed, setClaimed] = useState(["true", "false"]);
  const [error, setError] = useState(null);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [sortModel, setSortModel] = useState([
    { field: "display_name", sort: "asc" },
  ]);

  const [filterModel, setFilterModel] = useState({
    items: [],
    logicOperator: "and",
  });

  // --- Fetch data ---
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { page, pageSize } = paginationModel;
      const sort = sortModel[0] ?? {};

      const params = new URLSearchParams({
        area: areaId,
        page: page + 1,
        pageSize,
        sortColumn: sort.field,
        sortDirection: sort.sort,
        resourceType,
      });

      if (filterModel.items.length > 0) {
        params.set("filters", JSON.stringify(filterModel));
      }

      const url = `${process.env.REACT_APP_QM_AWS_API_GATEWAY_ENDPOINT}area/resources?${params.toString()}`;

      const res = await fetch(url, {
        headers: {
          Authorization: authState.accessToken.accessToken,
          Accept: "application/json",
        },
      });

      if (!res.ok) throw new Error(`Failed to fetch data: ${res.statusText}`);

      const data = await res.json();

      let resourceRows = [];
      switch (resourceType) {
        case "Mailbox":
          resourceRows = data.mailBoxes;
          break;
        case "OneDrive":
          resourceRows = data.oneDrives;
          break;
        default:
          resourceRows = data.teamsOrSharePoint;
      }

      setRows(resourceRows);
      setDepartments(data.departments);
      setDepartmentNumbers(data.departmentNumbers);
      setRowCount(data.totalRows);
    } catch (err) {
      console.error(err);
      setError(err.message || "Unknown error occurred");
      setRows([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    areaId,
    resourceType,
    paginationModel,
    sortModel,
    filterModel,
    authState.accessToken?.accessToken,
  ]);

  // IMPORTANT -- Prevents the server from getting spammed
  const fetchDataDebounced = useCallback(
      debounce(() => fetchData(), 600),
      [fetchData]
  );


  useEffect(() => {
    fetchDataDebounced();
    return fetchDataDebounced.clear;
  }, [fetchDataDebounced]);

  const columns = [
    { field: "id", headerName: "Resource ID", minWidth: 150, flex: 1},
    { field: "display_name", headerName: "Display Name", minWidth: 200, filterable: true, flex: 1 },
    { field: "owner_display_name", headerName: "Owner", minWidth: 200, filterable: true, flex: 1},
    { field: "owner_principal_name", headerName: "Owner Principal", minWidth: 200, filterable: true, flex: 1},,
    {
      field: "storage_used_byte",
      headerName: "Current Usage",
      minWidth: 150,
      valueFormatter: (value) => formatBytes(value, 2),
      filterable: false,
      flex: 1
    },
    { field: "report_refresh_date", headerName: "Last Refresh", minWidth: 200, filterable: false, flex: 1 },
    { field: "primary_role_employee", headerName: "Employee Type", minWidth: 150, filterable: true, flex: 1, type: "singleSelect", valueOptions: ["Data Issue", "Faculty/Staff", "Hourly Employee", "Shared Resources", "Student Employee", "Teams/SharePoint"]},
    { field: "department", headerName: "Department", minWidth: 150, filterable: true, flex: 1, type: "singleSelect", valueOptions: departments },
    { field: "department_number", headerName: "Department No.", minWidth: 150, filterable: true, flex: 1, type: "singleSelect", valueOptions: departmentNumbers },
    {
      field: "quota",
      headerName: "Quota",
      minWidth: 300,
      sortable: false,
      filterable: false,
      cellClassName: "centeredCell",
      renderCell: (params) => {
        return(
          <QuotaEntry id={params.id} type={resourceType} owner={params.row.owner_principal_name} employeeType={params.row.primary_role_employee}/>
        )
      },
      flex: 1
    },
    { field: "claimed", headerName: "Claimed", minWidth: 50, filterable: true, sortable: false, flex: 1, type: "singleSelect", valueOptions: claimed, cellClassName: "centeredCell",  renderCell: (params) => {
        return(
            <input type="checkbox" style={{marginLeft: "35%"}} disabled={true} checked={params.row.claimed === "true"} />
        )
      } }
  ];

  return (
    <Box sx={{ height: 600, width: "100%" }}>
      <DataGrid
        sx={{
          "& .MuiCircularProgress-root": { color: "#A60F2D" },
          "& .centeredCell": { display: "flex", alignItems: "center" },
        }}
        rows={rows}
        columns={columns}
        rowCount={rowCount}
        loading={loading}
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={(model) => {
          if (JSON.stringify(model) === JSON.stringify(filterModel)) return;
          setFilterModel(model);
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
        }}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        sortModel={sortModel}
        onSortModelChange={(model) => {
          setSortModel(model);
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
        }}
        pageSizeOptions={[10, 15, 25]}
        components={{
          NoRowsOverlay: () => (
            <Box sx={{ p: 2, textAlign: "center" }}>
              {error ? `Error: ${error}` : "No rows"}
            </Box>
          ),
        }}
      />
    </Box>
  );
};